import { LockClosedIcon, ReloadIcon, UpdateIcon } from '@radix-ui/react-icons'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { ViewToggle } from 'features/kestrelViewer/ui/tools/ViewToggle'
import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVNavigationMode } from 'types'

const NavigationModesContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SubModesContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  left: 100%;
  top: 0;
  display: flex;
  z-index: 10;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

const NAVIGATION_MODES: {
  mode: KVNavigationMode
  label: string
  icon: typeof ReloadIcon
}[] = [
  { mode: 'orbit', label: 'Orbit Mode', icon: ReloadIcon },
  { mode: 'fixedPivot', label: 'Pivot Mode', icon: UpdateIcon },
  { mode: 'locked', label: 'Locked Mode', icon: LockClosedIcon },
]

export const NavigationModes: FC = () => {
  const { sendCommand, navigationMode, setNavigationMode } =
    useKestrelViewerContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { kestrelModel } = useKestrel()
  const [showSubModes, setShowSubModes] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.relatedTarget as Node)
      ) {
        setShowSubModes(false)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      if (container) {
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  const handleModeChange = (newMode: KVNavigationMode) => {
    if (newMode === navigationMode) return

    setNavigationMode(newMode)

    sendCommand({
      command: KestrelCommands.SET_NAVIGATION_MODE,
      navigationMode: newMode,
    })

    trackButtonClicked({
      button_name: 'kestrel_set_navigation_mode',
      kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      navigation_mode: newMode,
    })

    setShowSubModes(false)
  }

  const iconProps = {
    style: {
      color: 'white',
      transition: 'color 0.2s ease-in-out',
    },
    width: 22,
    height: 22,
  }

  const currentMode = NAVIGATION_MODES.find((m) => m.mode === navigationMode)
  const otherModes = NAVIGATION_MODES.filter((m) => m.mode !== navigationMode)
  const CurrentIcon = currentMode?.icon ?? ReloadIcon

  return (
    <NavigationModesContainer
      ref={containerRef}
      onMouseEnter={() => setShowSubModes(true)}>
      <TooltipWrapper
        side={'top'}
        offset={5}
        label={currentMode?.label ?? 'Navigation Mode'}>
        <ViewToggle hasSubmenu onClick={() => setShowSubModes(!showSubModes)}>
          <CurrentIcon {...iconProps} />
        </ViewToggle>
      </TooltipWrapper>

      <SubModesContainer $isVisible={showSubModes}>
        {otherModes.map(({ mode, label, icon: Icon }) => (
          <TooltipWrapper key={mode} side={'top'} offset={5} label={label}>
            <ViewToggle onClick={() => handleModeChange(mode)}>
              <Icon {...iconProps} />
            </ViewToggle>
          </TooltipWrapper>
        ))}
      </SubModesContainer>
    </NavigationModesContainer>
  )
}
